import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '../../components/ui/Icon';
import { useAuthStore } from '../../stores/auth.store';
import { dealOwnersQuery, fullName } from './dealsApi';

/**
 * Popover "Prossima azione": cosa si fa, entro quando, chi lo fa.
 *
 * Non è un promemoria interno alla trattativa: crea un Task vero, che compare
 * in Task con la sua scadenza. È così che Andrea vede chi chiama chi senza
 * chiederlo a nessuno.
 */
const SUGGERIMENTI = ['Chiamare', 'Mandare il preventivo', 'Chiedere i documenti', 'Ricontattare'];

/** Data di default: fra due giorni lavorativi scarsi, cioè domani. */
function domani(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function NextActionPopover({ title, company, pending, onConfirm, onClose }: {
  /** Titolo suggerito, se arriva da fuori. */
  title?: string;
  /** Nome dell'azienda: entra nel titolo suggerito ("Chiamare Alfa Srl"). */
  company: string;
  pending?: boolean;
  onConfirm: (v: { title: string; dueDate: string | null; assigneeId: string | null }) => void;
  onClose: () => void;
}) {
  const me = useAuthStore((s) => s.user);
  const { data: owners } = useQuery(dealOwnersQuery);
  const [text, setText] = useState(title ?? `Chiamare ${company}`);
  const [dueDate, setDueDate] = useState(domani());
  const [assigneeId, setAssigneeId] = useState(me?.id ?? '');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    if (pending || !text.trim()) return;
    onConfirm({ title: text.trim(), dueDate: dueDate || null, assigneeId: assigneeId || null });
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        className="dropdown adm-pop right-0 mt-1 z-[61]"
        role="dialog"
        aria-label="Programma la prossima azione"
        style={{ width: 'min(420px, calc(100vw - 32px))' }}
      >
        <div className="adm-pop-hd">
          <span className="t-micro">Prossima azione</span>
          <span className="font-semibold truncate">{company}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Cosa</label>
          <input
            className="inp inp-sm"
            autoFocus
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          />
          <label className="field-label">Entro</label>
          <input className="inp inp-sm mono" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          <label className="field-label">Chi</label>
          <select className="inp inp-sm" value={assigneeId} onChange={(e) => setAssigneeId(e.target.value)}>
            <option value="">Nessuno</option>
            {(owners ?? []).map((u) => <option key={u.id} value={u.id}>{u.id === me?.id ? 'Io' : fullName(u)}</option>)}
          </select>
        </div>
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {SUGGERIMENTI.map((s) => (
            <button key={s} className="btn btn-sm btn-ghost" onClick={() => setText(`${s} ${company}`)}>{s}</button>
          ))}
        </div>
        <div className="adm-pop-ft">
          <span className="text-[12px] mute">Diventa un task in Task.</span>
          <div className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <button className="btn btn-sm btn-primary" disabled={pending || !text.trim()} onClick={submit}>
            <Icon name="check" size={10} /> Programma
          </button>
        </div>
      </div>
    </>
  );
}
